import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, FlexColBox } from "../../common/components";
import AddDesignToPreorderModal from "../AddDesignToPreorderModal/AddDesignToPreorderModal";
import OrderHistory from "./OrderHistory";
import { Timeline } from "react-event-timeline";
import { AdminOrderContext } from "../../context/Admin/Order";
import { ComboItems, ItemPricing } from "../../types";
import { Typography } from "@mui/material";
import { palette, spacing } from "../../styles/theme";
import { Switch } from "@mui/material";
import { sortPreorderItems } from "../../utils/SortingUtils";
import { isQuoteRequest } from "../../utils/CartUtils";
import OrderItemDetails from "./OrderItemDetails";
import OrderDetails from "./OrderDetails";
import { OrderContext } from "../../context/Order/OrderContext";
import { useLocation } from "react-router-dom";
import { useTenant } from "../../context/Tenant";

const Order = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const isFromAdmin = location.pathname.includes("/admin");
  const { tenant } = useTenant();
  const { orderDetail: preorderDetailFromAdmin } =
    useContext(AdminOrderContext);
  const { orderDetail: preorderDetailFromUser } = useContext(OrderContext);

  const preorderDetail = isFromAdmin
    ? preorderDetailFromAdmin
    : preorderDetailFromUser;

  const [openAddDesignModal, setOpenAddDesignModal] = React.useState(false);
  const [showDistributorPrices, setShowDistributorPrices] = useState(
    !!preorderDetail?.isDistributor || !!preorderDetail?.showDistributorPrices
  );

  const [addDesignSuccess, setAddDesignSuccess] = useState(false);

  const closeAddDesignModalClick = useCallback(
    (success: boolean) => {
      setOpenAddDesignModal(false);
      setAddDesignSuccess(success);
    },
    [setOpenAddDesignModal, setAddDesignSuccess]
  );

  const onClickOpenAddDesignModel = useCallback(() => {
    setOpenAddDesignModal(true);
  }, [setOpenAddDesignModal]);

  const onClickPrint = useCallback(() => {
    window.print();
  }, []);

  const mappedItemsWithComboItems = preorderDetail?.pricing.comboItems.reduce(
    (acc, comboItem) => {
      return [
        ...acc,
        ...comboItem.items.map((item) => ({ ...item, comboItem })),
      ];
    },
    [] as (ItemPricing & { comboItem: ComboItems })[]
  );

  const isQuoteRequestForOrder = isQuoteRequest(
    preorderDetail?.pricing,
    preorderDetail?.techpack
  );

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "none",
          "@media print": {
            display: "flex",
            position: "absolute",
            top: "8px",
            left: 0,
            right: 0,
            justifyContent: "center",
            zIndex: 1,
          },
        }}
      >
        <img
          src={tenant.logo.logo}
          alt={`${tenant.tenantName} logo`}
          style={{ maxHeight: "28px", width: "auto" }}
        />
      </Box>
      <Box
        display="flex"
        gap="48px"
        fullWidth
        justifyContent="flex-end"
        alignItems={{ xs: "center", lg: "flex-start" }}
        px="24px"
        flexDirection={{ xs: "column", lg: "row" }}
        sx={{
          "@media print": {
            gap: "24px",
            px: 0,
            pt: "44px",
            justifyContent: "flex-start",
            alignItems: "stretch",
            flexDirection: "column",
          },
        }}
      >
        <FlexColBox
          gap="32px"
          sx={{
            minWidth: { xs: "100%", md: "540px" },
            maxWidth: { xs: "100%", md: "80%", lg: "70%", xxl: "50%" },
            width: "100%",
            "@media print": {
              minWidth: "100%",
              maxWidth: "100%",
            },
          }}
        >
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h1">
              {isQuoteRequestForOrder
                ? t("order.quote_request")
                : t("order.title")}
            </Typography>
            <Box display="flex" gap="12px">
              {!!preorderDetail?.pricing.distributor && (
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={"4px"}
                  px={{ xs: spacing.wrapperMobile, lg: 0 }}
                  mt={{ xs: spacing.wrapperMobile, lg: 0 }}
                  sx={{
                    "@media print": {
                      display: showDistributorPrices ? "flex" : "none",
                      px: 0,
                      mt: 0,
                    },
                  }}
                >
                  <Switch
                    id="showDistributorPrices"
                    checked={showDistributorPrices}
                    onChange={(_, checked) => setShowDistributorPrices(checked)}
                  />
                  <Typography
                    variant="textSm"
                    component="label"
                    htmlFor="showDistributorPrices"
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {t("order.distributor_pricing")}
                  </Typography>
                </Box>
              )}
              {isFromAdmin && (
                <React.Fragment>
                  <Box sx={{ displayPrint: "none" }}>
                    <Button onClick={onClickOpenAddDesignModel} size="small">
                      {t("order.add_design")}
                    </Button>
                  </Box>
                  <Box sx={{ displayPrint: "none" }}>
                    <Button onClick={onClickPrint} size="small">
                      {t("order.print")}
                    </Button>
                  </Box>
                </React.Fragment>
              )}
            </Box>
          </Box>
          {addDesignSuccess && (
            <Alert
              icon="check_circle"
              message={t("order.add_design_success")}
              type={"success"}
            />
          )}
          <Box display="flex" flexDirection="column" gap="12px">
            {(() => {
              // Check if any item has factory info
              const hasFactoryInfo = mappedItemsWithComboItems?.some(
                (item) => item.factory && item.factoryPosition
              );

              // Sort items - if factory info exists, sort by factory first
              const sortedItems = hasFactoryInfo
                ? mappedItemsWithComboItems?.sort((a, b) => {
                    // First sort by factory
                    if (a.factory && b.factory && a.factory !== b.factory) {
                      return a.factory.localeCompare(b.factory);
                    }
                    // Then sort by factory position
                    if (a.factoryPosition && b.factoryPosition) {
                      return a.factoryPosition - b.factoryPosition;
                    }
                    return 0;
                  })
                : mappedItemsWithComboItems?.sort(sortPreorderItems);

              return sortedItems?.map((item, itemIndex) => {
                // Use factory info if available, otherwise use index-based numbering
                const displayInfo =
                  item.factoryInfo ||
                  `${itemIndex + 1}/${sortedItems?.length ?? 0}`;

                return (
                  <OrderItemDetails
                    key={item.designId}
                    item={item}
                    title={`${item.projectName} - ${item.designName} (${displayInfo})`}
                    comboItems={item.comboItem}
                    orderDate={preorderDetail?.preorder?.createdAt}
                    preorderDetail={preorderDetail}
                    showDistributorPrices={showDistributorPrices}
                  />
                );
              });
            })()}
          </Box>

          {isFromAdmin && (
            <FlexColBox gap="24px" padding="24px" sx={{ displayPrint: "none" }}>
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "medium",
                  color: palette.grey.nearBlack,
                }}
              >
                {t("order.timeline")}
              </Typography>
              <Timeline
                style={{
                  paddingTop: "4px",
                  width: "100%",
                }}
                lineStyle={{
                  backgroundColor: palette.grey.darkGrey,
                  left: "2px",
                }}
              >
                {preorderDetail?.history?.map(
                  (historyItem, historyItemIndex) => (
                    <OrderHistory
                      historyItem={historyItem}
                      key={historyItemIndex}
                      isLast={
                        historyItemIndex ===
                        (preorderDetail?.history?.length ?? 0) - 1
                      }
                    />
                  )
                )}
              </Timeline>
            </FlexColBox>
          )}
        </FlexColBox>

        {preorderDetail && (
          <OrderDetails showDistributorPrices={showDistributorPrices} />
        )}
      </Box>
      <AddDesignToPreorderModal
        open={openAddDesignModal}
        onClose={closeAddDesignModalClick}
      />
    </React.Fragment>
  );
};

export default Order;
